/**
 * Fala 2 kokpitu decyzji — WSPÓLNY hak leniwego doładowania wierszy koszyka
 * grupowania. Wyciągnięty z trzech niemal identycznych bloków w
 * `MyPendingDecisionsTab.tsx` / `ActiveInstancesTab.tsx` / `MySubmittedInstancesTab.tsx`
 * (finding [E] pre-commit review PR wprowadzającego grupowanie — duplikacja ~80 linii × 3).
 *
 * Kontrakt: koszyki startują ZWINIĘTE (`expandedGroupKeys` — pusty zbiór na starcie,
 * klucz OBECNY w zbiorze = koszyk ROZWINIĘTY). Rozwinięcie koszyka odpala OSOBNE
 * żądanie o jego wiersze (`fetchRows`, przez `useQueries` — zbiór rozwiniętych kluczy
 * jest dynamiczny, stąd nie zwykły `useQuery` w pętli), z WŁASNĄ mini-paginacją per
 * klucz (`groupRowsOffset`, wewnętrzny stan hooka). Zmiana wymiaru grupowania (lub
 * wyjście z trybu grupowanego) MUSI wyczyścić ten stan — wołający odpala to przez
 * zwrócone `resetExpansion()` ze swojego handlera zmiany wymiaru (klucze koszyków są
 * specyficzne dla wymiaru: id procesu vs enum priorytetu vs…, więc zostawiony stan z
 * poprzedniego wymiaru byłby martwy — i teoretycznie mógłby kolidować, gdyby dwa
 * wymiary wylosowały identyczny string klucza).
 *
 * Różnice per ekran zostają PO STRONIE WOŁAJĄCEGO (parametry hooka) — hook jest
 * celowo pozbawiony pojęć domenowych (waluty/scope/overdue/status):
 *  - `fetchRows` domyka nad parametrami zapytania specyficznymi dla ekranu
 *    (headerFilters/overdue/scope/status…) — hook nie zna ich nazw ani kształtu.
 *  - `queryKeyExtra` MUSI zawierać dokładnie te same zmienne parametry, które
 *    `fetchRows` czyta z domknięcia — inaczej cache react-query się rozjedzie (stare
 *    wiersze pod nowym filtrem/zakresem). Parametry STAŁE w czasie życia komponentu
 *    (np. `status: 'active'` na ekranie „Aktywne") NIE muszą wchodzić do klucza.
 *  - `buildSummary` — TYLKO „Moje czekające" pokazuje sumę walutową w nagłówku
 *    koszyka (kolumna „Wartość"); pozostałe dwa ekrany nie mają tej kolumny i
 *    pomijają parametr → `summary: null`.
 *  - `testIdPrefix` — prefiks `data-testid` błędu/paginacji footera koszyka
 *    (`approvals`/`active`/`submitted`, różny per ekran, zgodny z resztą testidów tej
 *    zakładki).
 */

import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DecisionGroupBy } from '@saas/shared';
import { keepPreviousData, useQueries } from '@tanstack/react-query';

import type { ConfigurableTableProps } from '@/components/configurable-table';
import type { DecisionGroupHeader } from '@/lib/processes/approvals-api';

import { groupLabelText } from './InstanceListingTable';
import { PaginationControls } from './PaginationControls';

/** Domyślny rozmiar strony dociąganych LENIWIE wierszy rozwiniętego koszyka grupowania. */
const DEFAULT_GROUP_ROWS_LIMIT = 20;

/**
 * Odpowiedź fetchera wierszy koszyka — wspólny podzbiór kształtu obu endpointów
 * (`MyPendingDecisionsResponse`/`InstancesListResponse` niosą więcej pól — `limit`/
 * `offset`/ewentualnie `sumsByCurrency` — ale hook potrzebuje tylko `data`/`total`).
 */
export interface LazyGroupRowsResponse<TRow> {
  data: TRow[];
  total: number;
}

export interface UseLazyGroupRowsParams<TRow> {
  /** Aktywny wymiar grupowania — `null` = tryb bez grupowania (`mappedGroups` wtedy `undefined`). */
  groupBy: DecisionGroupBy | null;
  /** Nagłówki koszyków (liczność + suma) — WEJŚCIE do mapowania, np. `groupedQuery.data?.groups ?? []`. */
  groups: readonly DecisionGroupHeader[];
  /** Dodatkowy warunek per-ekran wpięty w `enabled` zapytań o wiersze (np. `!isAll` na „Aktywne"). */
  enabled: boolean;
  /** Bazowy queryKey (BEZ `groupBy` i obiektu parametrów) — np. `['processes','my-pending-decisions','group-rows']`. */
  queryKeyBase: readonly unknown[];
  /**
   * Zmienne parametry WCHODZĄCE do zapytania (headerFilters/overdue/scope/status…) —
   * MUSZĄ mirror'ować dokładnie to, co `fetchRows` czyta z domknięcia, inaczej cache
   * react-query się rozjedzie. Parametry stałe (niezmienne w czasie życia komponentu)
   * NIE muszą tu wchodzić.
   */
  queryKeyExtra: Record<string, unknown>;
  /** Fetcher rozwinięcia JEDNEGO koszyka — domyka nad parametrami specyficznymi dla ekranu. */
  fetchRows: (params: {
    groupKey: string;
    offset: number;
    limit: number;
  }) => Promise<LazyGroupRowsResponse<TRow>>;
  /** Buduje slot podsumy koszyka (np. suma kwot per waluta). Pominięty → `summary: null`. */
  buildSummary?: (group: DecisionGroupHeader) => ReactNode;
  /** Prefiks `data-testid` błędu/paginacji footera koszyka (`${testIdPrefix}-group-rows-error-<key>` / `${testIdPrefix}-group-pagination-<key>`). */
  testIdPrefix: string;
  /** Rozmiar strony wewnątrz koszyka. Domyślnie 20. */
  limit?: number;
}

export interface UseLazyGroupRowsResult<TRow> {
  /** Rozwinięte koszyki — klucz OBECNY = koszyk ROZWINIĘTY (domyślnie puste = wszystkie zwinięte). */
  expandedGroupKeys: Set<string>;
  /** Przełącza rozwinięcie koszyka o danym kluczu. */
  toggleGroupExpand: (key: string) => void;
  /** Czyści rozwinięcie + paginację koszyków — wołaj przy zmianie wymiaru grupowania/wyjściu z trybu grupowanego. */
  resetExpansion: () => void;
  /** Koszyki zmapowane na kształt `ConfigurableTable.groups` (`undefined` gdy `groupBy===null`). */
  mappedGroups: ConfigurableTableProps<TRow>['groups'];
}

/**
 * Leniwe doładowanie wierszy koszyka grupowania — WSPÓLNY hak dla 3 zakładek kokpitu
 * decyzji. Patrz komentarz nagłówkowy pliku po pełny kontrakt i różnice per ekran.
 */
export function useLazyGroupRows<TRow>({
  groupBy,
  groups,
  enabled,
  queryKeyBase,
  queryKeyExtra,
  fetchRows,
  buildSummary,
  testIdPrefix,
  limit = DEFAULT_GROUP_ROWS_LIMIT,
}: UseLazyGroupRowsParams<TRow>): UseLazyGroupRowsResult<TRow> {
  const { t } = useTranslation();

  // Rozwinięte koszyki — klucz OBECNY = koszyk ROZWINIĘTY; domyślnie ZWINIĘTE (zbiór
  // pusty). `groupRowsOffset` — paginacja WEWNĄTRZ koszyka (per klucz), niezależna od
  // ewentualnej paginacji trybu płaskiego (ta zostaje po stronie wołającego).
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());
  const [groupRowsOffset, setGroupRowsOffset] = useState<Record<string, number>>({});

  function toggleGroupExpand(key: string) {
    // Zwinięcie koszyka NIE czyści `groupRowsOffset[key]` ŚWIADOMIE — pozycja (strona) koszyka
    // zostaje zapamiętana i wraca przy ponownym rozwinięciu; kasuje ją dopiero `resetExpansion()`
    // wołane przy zmianie wymiaru grupowania.
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function resetExpansion() {
    setExpandedGroupKeys(new Set());
    setGroupRowsOffset({});
  }

  // Wiersze rozwiniętych koszyków — JEDNO żądanie per rozwinięty koszyk (`useQueries`,
  // liczba zapytań = `expandedGroupKeys.size`, dynamiczna — stąd `useQueries` zamiast
  // pętli `useQuery`), z WŁASNĄ paginacją (`groupRowsOffset[key]`). Kolejność wyniku
  // odpowiada kolejności `expandedKeysArray` (kontrakt `useQueries`) — zip z powrotem
  // do mapy po kluczu niżej.
  const expandedKeysArray = useMemo(() => [...expandedGroupKeys], [expandedGroupKeys]);
  const groupRowsQueries = useQueries({
    queries: expandedKeysArray.map((key) => ({
      queryKey: [
        ...queryKeyBase,
        groupBy,
        {
          key,
          offset: groupRowsOffset[key] ?? 0,
          limit,
          ...queryKeyExtra,
        },
      ],
      queryFn: () => fetchRows({ groupKey: key, offset: groupRowsOffset[key] ?? 0, limit }),
      enabled,
      // Zmiana strony koszyka (klik mini-paginacji) zmienia `groupRowsOffset[key]` →
      // nowy klucz cache; `keepPreviousData` trzyma wiersze i stopkę poprzedniej strony
      // do czasu przybycia nowej (bez migotania spinnerem i znikania przycisku
      // paginacji pod kursorem).
      placeholderData: keepPreviousData,
    })),
  });
  // BEZ `useMemo` — `groupRowsQueries` jest nową tablicą co render (`useQueries`), więc
  // memoizacja po samym `expandedKeysArray` zatrzasnęłaby WYNIKI zapytań (status
  // loading→success) ze stanu sprzed re-renderu wywołanego przez react-query (klucze
  // rozwinięcia się nie zmieniają, ale ISTOTNE jest to, co query zwraca). Koszt
  // pominięcia memoizacji jest pomijalny — mapa ma najwyżej tyle wpisów, ile
  // rozwiniętych koszyków.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groupRowsByKey = new Map<string, (typeof groupRowsQueries)[number]>();
  expandedKeysArray.forEach((key, i) => {
    const q = groupRowsQueries[i];
    if (q) groupRowsByKey.set(key, q);
  });

  // Mapowanie koszyków serwera na generyczny kształt `ConfigurableTable.groups` —
  // etykieta per wymiar (`groupLabelText`) + opcjonalna podsuma (`buildSummary`, TYLKO
  // „Moje czekające" ją podaje — pozostałe ekrany dostają `summary: null`). Wiersze
  // koszyka są PUSTE, dopóki user go nie rozwinie — `isLoading`/`footer` sterują
  // wskaźnikiem ładowania i mini-paginacją wewnątrz koszyka (patrz `ConfigurableTable.tsx`).
  // Ten `useMemo` PRZELICZA SIĘ CO RENDER — `groupRowsByKey` w deps jest świeżą `Map` co render
  // (patrz komentarz przy jej budowie wyżej), więc nic tu nie jest realnie oszczędzane; zostaje
  // dla czytelności/spójności typu zwracanego, nie jako optymalizacja.
  const mappedGroups = useMemo(() => {
    if (groupBy === null) return undefined;
    return groups.map((g) => {
      const expanded = expandedGroupKeys.has(g.key);
      const rowsQuery = expanded ? groupRowsByKey.get(g.key) : undefined;
      const rowsData = rowsQuery?.data;
      return {
        key: g.key,
        label: groupLabelText(t, groupBy, g),
        count: g.count,
        rows: rowsData?.data ?? [],
        isLoading: expanded && rowsQuery !== undefined && rowsQuery.isLoading,
        summary: buildSummary ? buildSummary(g) : null,
        footer:
          expanded && rowsQuery?.isError ? (
            <span
              className="text-sm text-error"
              data-testid={`${testIdPrefix}-group-rows-error-${g.key}`}
            >
              {t('tenant.processes.approvals.groupBy.rowsLoadError')}
            </span>
          ) : expanded && rowsData && rowsData.total > limit ? (
            <PaginationControls
              total={rowsData.total}
              offset={groupRowsOffset[g.key] ?? 0}
              limit={limit}
              onOffsetChange={(next) => setGroupRowsOffset((prev) => ({ ...prev, [g.key]: next }))}
              testId={`${testIdPrefix}-group-pagination-${g.key}`}
            />
          ) : undefined,
      };
    });
  }, [
    groupBy,
    groups,
    expandedGroupKeys,
    groupRowsByKey,
    groupRowsOffset,
    t,
    buildSummary,
    testIdPrefix,
    limit,
  ]);

  return { expandedGroupKeys, toggleGroupExpand, resetExpansion, mappedGroups };
}
